import { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { Check, X, Mail, Phone } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export function AccessRequestsTab() {
  const [status, setStatus] = useState("pending");
  const [data, setData] = useState(null);
  const [roles, setRoles] = useState({});
  const load = () => api.get(`/admin/access-requests?status=${status}`).then(r => setData(r.data)).catch(() => setData({ requests: [], pending: 0 }));
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async (r) => {
    const role = roles[r.id] || "coach";
    if (!window.confirm(`Create a ${role} account for ${r.name} (${r.email}) and email them a welcome invite?`)) return;
    try {
      const { data: d } = await api.post(`/admin/access-requests/${r.id}/approve`, { role });
      d.email_sent ? toast.success(`Account created · invite emailed to ${r.email}`) : toast.warning(`Account created but email failed — temp password: ${d.temp_password}`, { duration: 30000 });
      window.dispatchEvent(new Event("admin-queue-changed"));
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't approve"); }
  };
  const decline = async (r) => {
    const note = window.prompt(`Decline ${r.name}'s request? Optional note to include in the email:`, "");
    if (note === null) return;
    try { await api.post(`/admin/access-requests/${r.id}/decline`, { note: note || null }); toast.success("Declined · they've been emailed"); window.dispatchEvent(new Event("admin-queue-changed")); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't decline"); }
  };

  return (
    <div className="space-y-6" data-testid="access-requests-tab">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">New user queue</div>
          <h2 className="font-display font-black text-3xl mt-1">Access requests</h2>
          <p className="opacity-60 text-sm mt-1">People who tapped "Message support" on the sign-in page. Approve to create their account and send the welcome invite.</p>
        </div>
        <div className="inline-flex rounded border border-border overflow-hidden" data-testid="access-requests-status">
          {[["pending", `Pending${data?.pending ? ` (${data.pending})` : ""}`], ["approved", "Approved"], ["declined", "Declined"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setStatus(k)} data-testid={`access-requests-filter-${k}`} className={`px-3 py-1.5 text-[10px] tracking-widest uppercase ${status === k ? "bg-[#83B68B] text-white" : "hover:text-[#83B68B]"}`}>{l}</button>
          ))}
        </div>
      </div>
      {!data ? <div className="opacity-50 font-display tracking-widest">LOADING…</div>
        : data.requests.length === 0 ? <div className="card-tactical rounded-md p-10 text-center opacity-60" data-testid="access-requests-empty">Nothing {status} right now.</div>
        : (
          <div className="space-y-2" data-testid="access-requests-list">
            {data.requests.map(r => (
              <div key={r.id} className="card-tactical rounded-md p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`access-request-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="font-display font-black text-lg">{r.name}</span>
                    <span className="text-[10px] tracking-widest uppercase text-[#83B68B]">{r.team_or_role}</span>
                    <span className="text-[10px] opacity-50">{format(parseISO(r.created_at), "d MMM yyyy HH:mm")}</span>
                  </div>
                  <div className="text-xs opacity-70 flex flex-wrap gap-3 mt-0.5">
                    <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 hover:text-[#83B68B]"><Mail className="w-3 h-3" /> {r.email}</a>
                    {r.phone && <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1 hover:text-[#83B68B]"><Phone className="w-3 h-3" /> {r.phone}</a>}
                  </div>
                  {r.message && <p className="text-sm opacity-80 mt-2 whitespace-pre-wrap">{r.message}</p>}
                  {r.status !== "pending" && <div className="text-[10px] tracking-widest uppercase opacity-50 mt-2">{r.status} by {r.decided_by || "admin"} · {r.decided_at ? format(parseISO(r.decided_at), "d MMM HH:mm") : ""}{r.role ? ` · ${r.role}` : ""}{r.note ? ` · "${r.note}"` : ""}</div>}
                </div>
                {r.status === "pending" && (
                  <div className="flex flex-wrap items-center gap-2">
                    <Select value={roles[r.id] || "coach"} onValueChange={v => setRoles(s => ({ ...s, [r.id]: v }))}>
                      <SelectTrigger data-testid={`access-request-role-${r.id}`} className="w-36 h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="coach">Coach</SelectItem><SelectItem value="member">Member</SelectItem><SelectItem value="vendor">Vendor</SelectItem><SelectItem value="treasurer">Treasurer (finance only)</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
                    </Select>
                    <button type="button" onClick={() => approve(r)} data-testid={`access-request-approve-${r.id}`} className="btn-volt px-4 py-2 rounded text-[10px] tracking-widest uppercase inline-flex items-center gap-1"><Check className="w-3 h-3" /> Approve</button>
                    <button type="button" onClick={() => decline(r)} data-testid={`access-request-decline-${r.id}`} className="px-4 py-2 rounded border border-red-400/50 text-red-300 text-[10px] tracking-widest uppercase inline-flex items-center gap-1 hover:bg-red-500/10"><X className="w-3 h-3" /> Decline</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
